import { useEffect, useRef, useState } from 'react';

import { createRequestLifecycle, type RequestLifecycle } from './requestLifecycle';
import type { CriteriaFacetStates, SearchPageCriteria } from './sharedTypes';

/**
 * The payload shape a facets fetcher resolves with. This is the page's `facetsOnly` route
 * response: `facetState` is absent on facet failure, and `facetsOnly: true` is the
 * deterministic response marker the reverse-deploy-skew guard keys on.
 */
export type SearchFacetsFetchResult<FilterName extends string> = {
  facetState?: CriteriaFacetStates<FilterName> | null;
  /** Matches the wire carriers' `facetsOnly?: true` — the marker is either echoed or absent. */
  facetsOnly?: true;
};

export type SearchFacetsFetcher<FilterName extends string> = (
  criteria: SearchPageCriteria<FilterName>,
  options: { signal?: AbortSignal }
) => Promise<SearchFacetsFetchResult<FilterName>>;

export type UseSearchFacetsStateOptions<FilterName extends string> = {
  /** False where narrowing UI cannot render; flipping false aborts any in-flight fetch. */
  enabled: boolean;
  /** Reuses the page's existing criteria serialization for change detection. */
  serializedCriteria: string;
  criteria: SearchPageCriteria<FilterName>;
  /**
   * Page-specific facetsOnly call. The abort signal is threaded to `fetch()` (matching the
   * family convention, `SearchListFetchArgs.signal`) so aborts cancel the HTTP request.
   */
  fetcher: SearchFacetsFetcher<FilterName>;
  /** Build-time seed, possibly undefined. */
  initialFacetState?: CriteriaFacetStates<FilterName>;
};

export type UseSearchFacetsStateResult<FilterName extends string> = {
  facetState: CriteriaFacetStates<FilterName> | undefined;
  /**
   * Exposed on all page surfaces — needed if a disable-options-during-fetch fallback is
   * ever adopted.
   */
  isFetching: boolean;
};

const isCriteriaEmpty = <FilterName extends string>(criteria: SearchPageCriteria<FilterName>) =>
  criteria.query === '' &&
  Object.values<string[]>(criteria.filters).every((values) => values.length === 0);

// Totality (every filter key present) is deliberately NOT checked here: a payload
// missing some filter keys still passes. That is acceptable defense-in-depth — every consumer
// reads slices via `facetState?.[name]`, so a missing key degrades to per-filter fail-open
// (that filter renders un-narrowed) rather than rejecting an otherwise-healthy payload.
const isValidFacetStatePayload = (facetState: unknown): boolean => {
  if (typeof facetState !== 'object' || facetState === null || Array.isArray(facetState)) {
    return false;
  }

  return Object.values(facetState).every(
    (facet) =>
      typeof facet === 'object' &&
      facet !== null &&
      !Array.isArray(facet) &&
      Object.values(facet).every((count) => typeof count === 'number')
  );
};

/**
 * Resolves a facetsOnly response into the three-state contract.
 *
 * Reverse-deploy-skew guard (deterministic marker): during a rolling deploy the new
 * bundle can hit a not-yet-updated server, which ignores the unknown `facetsOnly` param and
 * returns a normal results response WITHOUT the `facetsOnly: true` echo. No shape/items
 * heuristic can reliably distinguish that legacy payload from a genuine slim defined-empty
 * response, so detection is positive: marker missing → treat as no-`facetState` → fail-open.
 * Entry-value shape validation is kept as cheap defense-in-depth. Both emit a `console.warn`
 * (client-side observability — a persistent regression such as a proxy stripping the field
 * would otherwise disable narrowing sitewide while every server log stays green). The marker
 * and this guard are permanent — the exposure recurs at every future deploy.
 */
const resolveFacetStateFromResponse = <FilterName extends string>(
  response: SearchFacetsFetchResult<FilterName>
): CriteriaFacetStates<FilterName> | undefined => {
  if (response?.facetsOnly !== true) {
    console.warn(
      'useSearchFacetsState: facetsOnly response missing its `facetsOnly: true` marker — ' +
        'treating as unavailable (fail-open). If this persists outside a deploy window, the ' +
        'route or a proxy is dropping the marker.'
    );
    return undefined;
  }

  const { facetState } = response;

  if (facetState === undefined || facetState === null) {
    // Genuine facet failure on a new server — fail-open without warning noise.
    return undefined;
  }

  if (!isValidFacetStatePayload(facetState)) {
    console.warn(
      'useSearchFacetsState: facetsOnly response carried a malformed facetState payload — ' +
        'treating as unavailable (fail-open).'
    );
    return undefined;
  }

  return facetState;
};

/**
 * Criteria-level facet state.
 *
 * Normative fetch/skip behavior:
 * - Initial state is `initialFacetState` from the very first render, before `enabled` flips
 *   true (SSR HTML and first client paint already render seed-narrowed options).
 * - On `enabled` + every criteria change, resolves to either a network fetch (criteria
 *   non-empty) or the seed (criteria empty — the seed IS the settled facet state for empty
 *   criteria; a seedless page resolves to `undefined` = fail-open).
 * - Both paths invalidate in-flight work identically ("use seed" is a synchronously resolved
 *   request) so a slow response for previously-applied criteria can never land after a
 *   clear-all/back-navigation and overwrite the seed.
 * - Keeps the previous `facetState` while a fetch is in flight (no flicker).
 * - Fetch/HTTP error or a response without `facetState` → `undefined` → full static options.
 * - `enabled` flipping false aborts the outstanding controller; the state keeps its last
 *   value and is inert until re-enabled. In-flight work is aborted on unmount.
 */
export const useSearchFacetsState = <FilterName extends string>(
  options: UseSearchFacetsStateOptions<FilterName>
): UseSearchFacetsStateResult<FilterName> => {
  // Lazy init: `useRef(create...())` would allocate a discarded instance on every render.
  const lifecycleRef = useRef<RequestLifecycle | null>(null);
  if (lifecycleRef.current === null) {
    lifecycleRef.current = createRequestLifecycle();
  }
  const lifecycle = lifecycleRef.current;
  // The seed is the first-render value on purpose: it represents the build-time facet state
  // for empty criteria and must not drift with later prop changes.
  const seedRef = useRef(options.initialFacetState);
  const criteriaRef = useRef(options.criteria);
  const fetcherRef = useRef(options.fetcher);
  const [facetState, setFacetState] = useState<CriteriaFacetStates<FilterName> | undefined>(
    options.initialFacetState
  );
  const [isFetching, setIsFetching] = useState(false);

  criteriaRef.current = options.criteria;
  fetcherRef.current = options.fetcher;

  useEffect(() => {
    if (!options.enabled) {
      // Same invalidation rule as seed-resolution: abort in-flight work and supersede it.
      lifecycle.startRequest();
      setIsFetching(false);
      return;
    }

    const criteria = criteriaRef.current;

    if (isCriteriaEmpty(criteria)) {
      // Treat "use seed" as a synchronously resolved request: abort + bump the request id so
      // any slow in-flight response for previous criteria is dropped instead of overwriting.
      lifecycle.startRequest();
      setFacetState(seedRef.current);
      setIsFetching(false);
      return;
    }

    const handle = lifecycle.startRequest();

    setIsFetching(true);

    fetcherRef
      .current(criteria, { signal: handle.controller.signal })
      .then((response) => {
        if (lifecycle.isStale(handle)) {
          return;
        }

        setIsFetching(false);
        setFacetState(resolveFacetStateFromResponse(response));
      })
      .catch(() => {
        if (lifecycle.isStale(handle)) {
          return;
        }

        setIsFetching(false);
        setFacetState(undefined);
      });
  }, [options.enabled, options.serializedCriteria]);

  useEffect(() => {
    return () => {
      lifecycle.abort();
    };
  }, []);

  return {
    facetState,
    isFetching,
  };
};
